"use client"

import * as React from "react";
import { Tabs as TabsPrimitive } from "@/components/radix";

import { cn } from "@/lib/utils";
import styles from "./tabs.module.css";

// Segmented tab control. Size (sm | md | lg) is set once on the root
// `<Tabs size="…">` and read by `TabsList` / `TabsTrigger` via context.
// The active state is a single pill (`.indicator`) that SLIDES between
// triggers: `TabsList` measures the active trigger and positions the
// indicator, and CSS transitions animate the move (200ms). Styling matches
// the Auto Screenshot design system (Figma). See `tabs.module.css`.
type TabsSize = "sm" | "md" | "lg";

const TabsSizeContext = React.createContext<TabsSize>("md");

// CSS-module class maps are typed loosely; cast once for dynamic
// `list_${size}` / `trigger_${size}` lookups.
const s = styles as Record<string, string>;

// Avoid the SSR useLayoutEffect warning while still measuring before paint
// on the client (so the indicator never slides in from 0,0 on mount).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & { size?: TabsSize }
>(({ size = "md", ...props }, ref) => (
  <TabsSizeContext.Provider value={size}>
    <TabsPrimitive.Root ref={ref} {...props} />
  </TabsSizeContext.Provider>
))
Tabs.displayName = "Tabs"

type IndicatorRect = { left: number; top: number; width: number; height: number }

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, forwardedRef) => {
  const size = React.useContext(TabsSizeContext)
  const innerRef = React.useRef<HTMLDivElement | null>(null)
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    },
    [forwardedRef],
  )

  const [rect, setRect] = React.useState<IndicatorRect | null>(null)

  useIsomorphicLayoutEffect(() => {
    const list = innerRef.current
    if (!list) return
    const measure = () => {
      const active = list.querySelector<HTMLElement>('[data-state="active"]')
      if (!active) return
      setRect({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      })
    }
    // Batch geometry reads into the next animation frame so the
    // observer/fonts-ready triggers (which fire right after a style or layout
    // invalidation) don't force a synchronous reflow. Coalesces bursts of
    // triggers into a single read per frame.
    let rafId = 0
    const scheduleMeasure = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(() => {
        rafId = 0
        measure()
      })
    }
    // Initial measure runs synchronously inside the layout effect (before paint)
    // so the indicator is positioned on first render and never slides in from 0,0.
    measure()
    // Re-measure when the active tab changes (Radix flips data-state),
    // when the list/triggers resize, and after web fonts swap (widths shift).
    const mo = new MutationObserver(scheduleMeasure)
    mo.observe(list, { attributes: true, attributeFilter: ["data-state"], subtree: true })
    const ro = new ResizeObserver(scheduleMeasure)
    ro.observe(list)
    const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts
    fonts?.ready.then(scheduleMeasure).catch(() => {})
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId)
      mo.disconnect()
      ro.disconnect()
    }
  }, [size])

  return (
    <TabsPrimitive.List
      ref={setRefs}
      className={cn(s.list, s[`list_${size}`], className)}
      {...props}
    >
      <span
        className={s.indicator}
        data-ready={rect ? "true" : "false"}
        aria-hidden="true"
        style={
          rect
            ? {
                transform: `translate(${rect.left}px, ${rect.top}px)`,
                width: rect.width,
                height: rect.height,
              }
            : undefined
        }
      />
      {children}
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const size = React.useContext(TabsSizeContext)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(s.trigger, s[`trigger_${size}`], className)}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn(styles.content, className)} {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
